import type { ButtonProps } from '@chakra-ui/react';
import type { ExplicitAddress } from '@cronos-app/sdk';
import { ConfirmButton } from '@ui/components/SwapDialog/ConfirmButton';
import { toaster } from '@ui/components/Toaster';
import { DEX_NAME_SHORT } from '@ui/config/layer';
import { Token } from '@ui/config/token';
import { useAgentFunBalance } from '@ui/hooks/contracts/useTokenBalance';
import { useH2SwapMutation } from '@ui/hooks/h2/useH2SwapMutation';
import { useAmountInputError } from '@ui/hooks/useAmountInputError';
import { useGlobalChainLayer } from '@ui/hooks/useGlobalChainLayer';
import { Fraction } from 'bi-fraction';
import React from 'react';

import { useH2SwapForm } from './components/H2SwapForm/useH2SwapForm';
import { useH2SwapSlippageDialog } from './components/H2SwapSlippageDialog/useH2SwapSlippageDialog';
import {
  useH2SwapDialog,
  useH2SwapFromTokenInfo,
  useH2SwapToTokenInfo,
} from './useH2SwapDialog';

export const H2SwapConfirmButton = (props: ButtonProps) => {
  const { title, isLoading, onClick, disabled } = useConfirmButton();
  const fromToken = useH2SwapForm((s) => s.fromToken);
  const agentTokenAddress = useH2SwapDialog((s) => s.agentTokenAddress);
  const enteredAmountStr = useH2SwapForm((s) => s.enteredAmountStr);
  const error = useAmountInputError({
    fromToken: fromToken,
    tokenAddress: agentTokenAddress,
    enteredAmountStr: enteredAmountStr,
  });

  return (
    <ConfirmButton
      title={title}
      isLoading={isLoading}
      disabled={disabled || !!error}
      {...props}
      onClick={onClick}
    />
  );
};

const useConfirmButton = () => {
  const balance = useAgentFunBalance().data;
  const fromToken = useH2SwapForm((s) => s.fromToken);
  const amount = useH2SwapForm((s) => s.enteredAmount);
  const bondingAddress = useH2SwapDialog((s) => s.bondingTokenAddress);
  const fromTokenInfo = useH2SwapFromTokenInfo();
  const toTokenInfo = useH2SwapToTokenInfo();
  const slippage = useH2SwapSlippageDialog((s) => s.entered);
  const mode = fromToken === Token.Agent ? 'sell-agent' : 'buy-agent';
  const agentTokenInfo = mode === 'sell-agent' ? fromTokenInfo : toTokenInfo;
  const chainLayer = useGlobalChainLayer();

  const { mutateAsync: swapTokens, isLoading } = useH2SwapMutation();

  const disabled = isLoading || !balance || balance.lte(Fraction.ZERO);
  const onClick = async () => {
    useH2SwapDialog.setState({
      closable: false,
    });

    try {
      const result = await swapTokens({
        amountIn: amount,
        inputTokenAddress: fromTokenInfo?.address ?? '',
        inputTokenDecimals: fromTokenInfo?.decimals ?? 18,
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        outputTokenAddress: (toTokenInfo?.address ?? '') as ExplicitAddress,
        outputTokenDecimals: toTokenInfo?.decimals ?? 18,
        slippage,
        mode,
        bondingAddress,
      });
      toaster.create({
        type: 'success',
        title: '(＾▽＾) Swap successful',
        meta: {
          txHash: result?.hash,
        },
      });
      useH2SwapDialog.getState().close();
    } catch (e) {
      toaster.create({
        type: 'error',
        title: '(〒︿〒) Something went wrong!',
        description: `Failed to swap ${
          fromTokenInfo?.symbol ?? ''
        }, let’s try again!`,
      });
    } finally {
      useH2SwapDialog.setState({
        closable: true,
      });
    }
  };

  return {
    title: `${mode === 'sell-agent' ? 'Sell ' : 'Buy '}${
      agentTokenInfo?.symbol
    } via ${DEX_NAME_SHORT[chainLayer]}`,
    isLoading,
    onClick,
    disabled,
  };
};
